(ns accounts.events
  (:require
   [very.re-frame.core :as rf]
   [graphql.core :as qu]
   [oops.core :refer [oget]]
   [util.core :refer [un-edgify]]))


(defn- flatten-accounts [accounts]
  (let [internal (un-edgify :internal
                            :accounts
                            {:total-value (get-in accounts [:internal :total-value])
                             :accounts {}}
                            accounts)
        external (un-edgify :external
                            :accounts
                            {:total-value (get-in accounts [:external :total-value])
                             :accounts {}}
                            accounts)]
    {:internal internal
     :external external}))

(defn client-accounts-fetched [db [accounts]]
  (let [client-account-data (get-in accounts [:client :accounts])]
    (-> db
        (assoc :accounts (flatten-accounts client-account-data))
        (assoc :account-allocation (:allocation client-account-data))
        (assoc :average-performance (:avg-performance client-account-data))
        (assoc :total-performance (select-keys client-account-data [:assets :liabilities :total-value])))))

(defn load-flow []
  {:first-dispatch [:fetch-client-accounts]
   :rules [{:when :seen-all-of?
            :events [:client-accounts-fetched
                     :quovo-connections-fetched]
            :dispatch [:loader/hide]
            :halt? true}
           {:when :seen-any-of?
            :events [:client-accounts-fetch-errored
                     :quovo-connections-fetch-errored]
            :dispatch [:loader/hide]
            :halt? true}]})

(rf/reg-event-fx
 :boot-client-accounts
 (fn [cfx]
   {:dispatch [:loader/show]
    :async-flow (load-flow)}))

(rf/reg-event-fx
 :fetch-client-accounts
 (fn [cfx]
   {:graphql [{:gql (qu/client-accounts-details)
               :success [:client-accounts-fetched]
               :error [:client-accounts-fetch-errored]
               :cancellable? true}
              {:gql (qu/quovo-connections)
               :success [:quovo-connections-fetched]
               :error [:quovo-connections-fetch-errored]}]}))

(rf/reg-event-db
 :client-accounts-fetched
 client-accounts-fetched)

(rf/reg-event-db
  :client-accounts-fetch-errored
  (fn [db [{:keys [data errors]}]]
    (client-accounts-fetched db [data])))

(rf/reg-event-db
 :quovo-connections-fetched
 (fn [db [{:keys [client]}]]
   (assoc db :quovo-connections (:quovo-connections client))))

(rf/reg-event-db
 :quovo-connections-fetch-errored
 (fn [db [{:keys [errors]}]]
   (assoc db :quovo-connections :error)))

(rf/reg-sub
 :accounts
 (fn [db]
   (:accounts db)))

(rf/reg-sub
 :accounts/form
 :<- [:accounts]
 (fn [accs]
   (:form accs)))

(rf/reg-sub
 :account-allocation
 (fn [db]
   (:account-allocation db)))

(rf/reg-sub
 :average-performance
 (fn [db]
   (:average-performance db)))

(rf/reg-sub
 :total-performance
 (fn [db]
   (:total-performance db)))

(rf/reg-sub
 :quovo
 (fn [db]
   (:quovo db)))

(rf/reg-event-db
 :account-focused
 (fn [db [{:keys [color id]}]]
   (assoc-in db [:accounts :focused] {:id id
                                      :color color})))

(defn- focused-account [accs]
  (let [foc (:focused accs)
        [_ acc] (first (filter (fn [[id v]] (= id (:id foc))) (get-in accs [:internal :accounts])))]
    (merge foc acc)))

(rf/reg-sub
 :focused-account
 :<- [:accounts]
 focused-account)

(rf/reg-sub
 :internal-accounts
 :<- [:accounts]
 (fn [accs]
   (update (:internal accs) :accounts vals)))

(rf/reg-sub
 :external-accounts
 :<- [:accounts]
 (fn [accs]
   (update (:external accs) :accounts vals)))

(rf/reg-sub
 :manual-accounts
 :<- [:external-accounts]
 (fn [accs]
   (filter #(nil? (:quovo-connection-id %)) (:accounts accs))))

(rf/reg-sub
 :quovo-connections
 (fn [db]
   (:quovo-connections db)))

(defn load-quovo [cfx]
  (let [loaded? (get-in cfx [:db :quovo :script-loaded?])
        token (get-in cfx [:db :quovo :token])]
    (merge
     (when-not token
       {:graphql {:gql (qu/quovo-token)
                  :success [:quovo-token-acquired]
                  :error [:quovo-unavailable]}
        :db (update (:db cfx) :quovo dissoc :unavailable?)})
     (when-not loaded?
       {:insert-script {:src "https://connect.quovo.com/connect.js"
                        :on-load [:quovo-script-loaded]}
        :db (update (:db cfx) :quovo assoc :script-loaded? false)}))))

(rf/reg-event-fx
 :connect-account
 (fn [cfx]
   {:modal {:open 'accounts.quovo/quovo-modal}}))

(rf/reg-event-fx
 :abort-connect-account
 (fn [cfx]
   {:modal {:close 'accounts.quovo/quovo-modal}}))

(rf/reg-event-fx
 :quovo-token-acquired
 (fn [cfx [{:keys [client]}]]
   {:db (assoc-in (:db cfx) [:quovo :token] (:ui-token (:quovo-token client)))}))

(rf/reg-event-fx
 :quovo-unavailable
 (fn [cfx [{:keys [errors]}]]
   {:db (assoc-in (:db cfx) [:quovo :unavailable?] true)}))

(rf/reg-event-fx
 :quovo-script-loaded
 (fn [cfx]
   {:db (assoc-in (:db cfx) [:quovo :script-loaded?] true)}))

(rf/reg-event-fx
 :quovo-sync
 (fn [cfx [err evt]]
   (when (and (not err) (some? evt))
     {:graphql {:gql (qu/create-quovo-account
                      {:input {:quovo-connection-id (-> evt
                                                        :connection :id str)}})
                :success [:quovo-account-added]}})))

(rf/reg-event-fx
 :quovo-account-added
 (fn [cfx]
   {:graphql {:gql (qu/client-accounts-details)
              :success [:fetch-client-accounts]}}))

(rf/reg-event-fx
 :fix-quovo-connection
 (fn [cfx [conn]]
   {:modal {:open 'accounts.quovo/quovo-modal}
    :db (assoc-in (:db cfx) [:?? :connection-to-fix] conn)}))

(rf/reg-sub
 :connection-to-fix
 (fn [db]
   (get-in db [:?? :connection-to-fix])))

(rf/reg-event-fx
 :add-account
 [(rf/enrich-fx load-quovo)]
 (fn [cfx]
   {:modal {:open 'accounts.add-account/add-account-modal}}))

(defn reset-form [db v]
  (assoc-in db [:accounts :form] {:error false
                                  :message nil
                                  :submitting? false}))

(defn submit [db]
  (assoc-in db [:accounts :form] {:error false
                                  :message nil
                                  :submitting? true}))

(defn returned [db]
  (assoc-in db [:accounts :form :submitting?] false))

(rf/reg-event-fx
 :abort-add-account
 [(rf/enrich reset-form)]
 (fn [cfx]
   {:modal {:close 'accounts.add-account/add-account-modal}}))

(rf/reg-event-fx
 :add-external-account
 [(rf/enrich submit)]
 (fn [cfx [account]]
   (let [db (:db cfx)]
     {:graphql {:gql (qu/create-external-account {:input account})
                :success [:external-account-added]
                :failure [:account-add-errored]
                :error [:account-add-errored]}})))

(rf/reg-event-fx
 :external-account-added
 [(rf/enrich reset-form)]
 (fn [cfx [{account :create-external-account}]]
   {:modal {:close 'accounts.add-account/add-account-modal}
    :graphql {:gql (qu/client-accounts-details)
              :success [:fetch-client-accounts]}}))

(rf/reg-event-db
 :account-add-errored
 [(rf/enrich returned)]
 (fn [db [{:keys [errors]}]]
   (assoc-in db [:accounts :form] {:error true
                                   :message (get-in errors [0 :message])})))

(rf/reg-event-fx
 :delete-manual-account
 (fn [cfx [acc]]
   {:graphql {:gql (qu/delete-external-account {:input {:id (:id acc)}})
              :success [:fetch-client-accounts]}}))

(rf/reg-event-fx
 :disconnect-account
 (fn [cfx [acc]]
   {:graphql {:gql (qu/delete-quovo-connection {:input {:quovo-connection-id (:id acc)}})
              :success [:fetch-client-accounts]}}))

(rf/reg-event-fx
 :manage-accounts
 [(rf/enrich-fx load-quovo)]
 (fn [cfx]
   {:modal {:open 'accounts.manage-accounts/manage-accounts-modal}}))

(rf/reg-event-fx
 :abort-manage-accounts
 (fn [cfx]
   {:modal {:close 'accounts.manage-accounts/manage-accounts-modal}}))

(rf/reg-event-fx
 :edit-account
 (fn [cfx [acc]]
   {:modal {:open 'accounts.manage-accounts/edit-account-modal}
    :db (assoc-in (:db cfx) [:accounts :editing] (:id acc))}))

(defn- account-under-edit [accs]
  (let [[_ acc] (first
                 (filter
                  (fn [[id v]] (= id (:editing accs)))
                  (get-in accs [:external :accounts])))]
    acc))

(rf/reg-sub
 :account-under-edit
 :<- [:accounts]
 account-under-edit)

(rf/reg-event-fx
 :edit-manual-account
 (fn [cfx [edits]]
   (let [accs (:accounts (:db cfx))
         id (:id (account-under-edit accs))]
     {:graphql {:gql (qu/update-external-account {:input (assoc edits :id id)})
                :success [:account-edited]}})))

(rf/reg-event-fx
 :account-edited
 (fn [cfx [acc]]
   {:modal {:close 'accounts.manage-accounts/edit-account-modal}
    :graphql {:gql (qu/client-accounts-details)
              :success [:fetch-client-accounts]}}))
